import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { markReviewedAction } from '../../../redux/actions/orderActions'
import ReactQuill from 'react-quill'
import { uploadToCloudinary } from '../../../utils/uploadToCloudinary'
import store from "../../../store"

const OrderForm = ({ order }) => {
    const dispatch = useDispatch()

    const [amazon_review_link, setAmazon_review_link] = useState('')
    const [review_image_proof_url, setReview_image_proof_url] = useState('')
    const [review_image_proof_url_preview, setReview_image_proof_url_preview] = useState('')
    const [remarks, setRemarks] = useState('')

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (setImage, setPreview) => (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
        } else {
            alert("Only PNG and JPG files are allowed!");
        }
    };

    const handleFileChange = (setImage, setPreview) => (e) => {
        const file = e.target.files[0];
        if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
        } else {
            alert("Only PNG and JPG files are allowed!");
        }
    };

    const displayID = String((order?.id)).padStart(6, '0');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!review_image_proof_url) {
            toast.error("Please fill in all required fields");
            return;
        }
        try {
            store.dispatch({ type: "UPLOAD_START" });
            const reviewProofURL = await uploadToCloudinary(
                review_image_proof_url,
                "orders",
                1,
                1
            );
            const data = {
                amazon_review_link,
                review_image_proof_url: reviewProofURL,
                remarks,
                order_id: order.id,
            };
            dispatch(markReviewedAction(data))
            store.dispatch({ type: "UPLOAD_END" });
        } catch (err) {
            store.dispatch({ type: "UPLOAD_RESET" });
            toast.error("Image upload failed. Please Check your Internet Connection and then try again.");
        }
    }

    const handleReset = () => {
        setAmazon_review_link('')
        setReview_image_proof_url('')
        setReview_image_proof_url_preview('')
        setRemarks('')
    }

    useEffect(() => {
        if (order) {
            setAmazon_review_link(order?.amazon_review_link)
            setReview_image_proof_url(order?.review_image_proof_url)
            setReview_image_proof_url_preview(order?.review_image_proof_url)
            setRemarks(order?.remarks)
        }
    }, [order])

    return (
        <Fragment>
            <form action="" className="w-full lg:w-[60%] bg-white rounded-2xl p-6 lg:p-8 border border-zinc-200/80 shadow-sm flex flex-col gap-6" onSubmit={handleSubmit}>
                {/* Order Info Badges */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 bg-zinc-50 border border-zinc-100 rounded-xl p-4 flex flex-col gap-1">
                        <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Order ID</span>
                        <span className="text-lg font-black text-zinc-900">#{displayID}</span>
                    </div>
                    <div className="flex-1 bg-zinc-50 border border-zinc-100 rounded-xl p-4 flex flex-col gap-1">
                        <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Order Number</span>
                        <span className="text-lg font-bold text-emerald-600 font-mono">{order?.order_number}</span>
                    </div>
                </div>

                {/* Amazon Review Link */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-zinc-700">Amazon Review Link</label>
                    <input
                        type="text"
                        value={amazon_review_link}
                        onChange={(e) => setAmazon_review_link(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-zinc-200/80 shadow-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none text-sm font-medium text-zinc-700 placeholder-zinc-400 bg-white transition-all"
                        placeholder="Paste your Amazon review link here"
                    />
                </div>

                {/* Review Picture */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-zinc-700">Review Picture <span className="text-rose-500">*</span></label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Upload Area */}
                        <div
                            className={`border-2 border-dashed ${review_image_proof_url_preview ? 'border-zinc-200' : 'border-zinc-300'} hover:border-sky-500 bg-zinc-50/50 hover:bg-zinc-50 rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all min-h-[200px]`}
                            onDrop={handleDrop(setReview_image_proof_url, setReview_image_proof_url_preview)}
                            onDragOver={handleDragOver}
                            onClick={() => document.getElementById("reviewproofInput").click()}
                        >
                            <input
                                id="reviewproofInput"
                                type="file"
                                accept="image/png, image/jpeg"
                                onChange={handleFileChange(setReview_image_proof_url, setReview_image_proof_url_preview)}
                                className="hidden"
                            />
                            <div className="text-center flex flex-col items-center gap-2">
                                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-zinc-100 text-zinc-400">
                                    <i className="fa-regular fa-image text-xl"></i>
                                </div>
                                <div className="text-sm text-zinc-600">
                                    <span className="font-bold text-sky-600">Click to upload</span> or drag and drop
                                </div>
                                <div className="text-xs text-zinc-400">PNG or JPG only</div>
                            </div>
                        </div>

                        {/* Preview Area */}
                        {review_image_proof_url_preview && (
                            <div className="border border-zinc-100 rounded-2xl p-4 bg-zinc-50/50 flex items-center justify-center overflow-hidden min-h-[200px]">
                                <img
                                    src={review_image_proof_url_preview}
                                    alt="Preview"
                                    className="max-w-full max-h-[180px] object-contain rounded-lg shadow-sm"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Review Remarks */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold text-zinc-700">Review Remarks <span className="text-zinc-400 font-normal">(optional)</span></label>
                    <div className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-sky-500/20 focus-within:border-sky-500 transition-all">
                        <ReactQuill
                            theme="snow"
                            value={remarks}
                            onChange={(value) => setRemarks(value)}
                            className="text-sm"
                            style={{ height: '150px', marginBottom: '40px' }}
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, 3, false] }],
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ 'color': [] }, { 'background': [] }],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    ['clean']
                                ]
                            }}
                        />
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-start gap-4 mt-2">
                    <button 
                        className="px-6 py-2.5 bg-white border border-zinc-200 hover:border-zinc-300 text-zinc-600 hover:text-zinc-700 font-bold text-sm rounded-xl transition-all shadow-sm hover:shadow" 
                        onClick={handleReset} 
                        type="button"
                    >
                        Reset
                    </button>
                    <button 
                        className="px-6 py-2.5 bg-gradient-to-br from-sky-500 to-indigo-600 text-white font-bold text-sm rounded-xl transition-all shadow-sm hover:shadow-md hover:shadow-sky-500/10 hover:scale-[0.98]" 
                        type="submit"
                    >
                        Submit Review
                    </button>
                </div>
            </form>
        </Fragment>
    )
}

export default OrderForm